/**
 * Whether a string looks like an email address.
 *
 * Deliberately **loose**, and callers must treat a `true` as "worth sending",
 * never as "the server will accept this". The authority on an address is the
 * backend's Django `EmailValidator`: it takes `alex@localhost` and quoted local
 * parts that this rejects, and refuses one-letter TLDs and hyphen-edged labels
 * that this waves through. Reproducing that rule in TypeScript would be a second
 * copy of a fiddly standard, drifting from the first the next time either moves.
 *
 * So a caller needs somewhere for the addresses the two disagree about to go. In
 * the bulk user importer (NG-1294) that is a per-row error from the endpoint,
 * which is why one such address costs its row and not the whole file.
 *
 * Shared because it was four identical copies of one regex before this — every
 * screen that takes an email typed by hand — and four copies of "looks like an
 * address" is four rules that can drift apart.
 */

/** Non-empty local part, `@`, non-empty dotted domain — no whitespace anywhere. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Trims first, so a padded cell or a pasted address is judged on what it says. */
export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

/** Splits a typed list of addresses: commas or new lines, each trimmed, blanks
 *  dropped. Case is kept; compare with `toLowerCase()`. Shared by every invite
 *  dialog so they agree on what counts as one address. */
export function parseEmailList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((email) => email.trim())
    .filter(Boolean);
}
